import { useCallback, useEffect, useRef, useState } from "react";
import arrowImg from "../../assets/svg/Arrow.svg";
import downArrow from "../../assets/svg/DownArrow.svg";
import SortingICon from "../../assets/svg/SortingICon.svg";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import SubtaskArrow from "@/assets/svg/SubtaskArrow.svg";

export type ColumeDef = {
  className?: (data: any) => string;
  key: string;
  header: string;
  sorting?: boolean;
  onCellRender?: (data: any) => JSX.Element;
  onHeaderRender?: (header: string) => JSX.Element;
  colspan?:(data: any) =>number
};
export type Props = {
  columnDef?: ColumeDef[];
  data: any[];
  className?: string;
  tableClassName?: string;
  hidePagination?: boolean;
  hideHeaders?: boolean;
  onAccordionRender?: (data: any) => JSX.Element;
  expandAll?: boolean;
  onRowClassName?: (data: any) => string;
  showDropDown?: (data: any) => string;
  showDropDownArrow?: (data: any) => string;
  showDropDownImg?: (data: any) => string;
  hederClassName?: string;
  noDataFound?: (data: any) => JSX.Element;
  showBody?: boolean;
  notShowNoDataFound?: boolean;
  dataToShow?: (data: any) => void;
};

const pageOptions = [
  { label: 10, value: 10 },
  { label: 25, value: 25 },
  { label: 50, value: 50 },
  { label: 100, value: 100 },
];

function Table(props: Props) {
  const { t } = useTranslation();
  const { columnDef, data, className, showBody = true } = props;
  const [dataSource, setDataSource] = useState(data);

  const [dataPerPage, setDataPerPage] = useState(pageOptions[0].value);
  const [pages, setPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [ascendingToggle, setAscendingToggle] = useState<boolean>(false);
  const [accordions, setAccordionshow] = useState<number[]>([]);
  const table = useRef<HTMLDivElement>(null);
  const tableRow = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    let count = 1;
    if (!props.hidePagination) {
      setDataSource(data.slice(0, dataPerPage));
      count = Math.ceil(data.length / dataPerPage);
      setPages(count);
    } else {
      setDataSource(data);
    }
    if (count > 1) {
      const [startIndex, endIndex] = calculateIndices(currentPage);
      changePage(currentPage, startIndex, endIndex);
    } else {
      setCurrentPage(1);
    }
  }, [data, dataPerPage, props.hidePagination, props]);

  const nextPage = () => {
    if (currentPage < pages) {
      const newCurrentPage = currentPage + 1;
      const [startIndex, endIndex] = calculateIndices(newCurrentPage);
      changePage(newCurrentPage, startIndex, endIndex);
    }
  };
  const previousPage = () => {
    if (currentPage >= 1) {
      const newCurrentPage = currentPage - 1;
      const [startIndex, endIndex] = calculateIndices(newCurrentPage);
      changePage(newCurrentPage, startIndex, endIndex);
    }
  };

  const reactSelectStyle = {
    control: (
      provided: Record<string, unknown>,
      state: { isFocused: boolean }
    ) => ({
      ...provided,
      border: "1px solid #E7E7E7",
      paddingTop: "0.2rem",
      paddingBottom: "0.2rem",
      outline: state.isFocused ? "2px solid #299d91" : "0px solid #E7E7E7",
      boxShadow: state.isFocused ? "0px 0px 0px #299d91" : "none",
      "&:hover": {
        outline: state.isFocused ? "1px solid #299d91" : "1px solid #E7E7E7",
        boxShadow: "0px 0px 0px #299d91",
      },
    }),
    option: (
      provided: Record<string, unknown>,
      state: { isSelected: boolean }
    ) => ({
      ...provided,
      color: state.isSelected ? "white" : "black",
      background: state.isSelected ? "#299d91" : "white",
      "&:hover": {
        color: "white",
        background: "#299d9199",
      },
    }),
  };

  const calculateIndices = useCallback(
    (page: number) => {
      const startIndex = (page - 1) * dataPerPage;
      const endIndex = startIndex + dataPerPage;
      return [startIndex, endIndex];
    },
    [dataPerPage]
  );

  const changePage = useCallback(
    (newPage: number, startIndex: number, endIndex: number) => {
      setCurrentPage(newPage);
      setDataSource(data.slice(startIndex, endIndex));
    },
    [data]
  );

  function dynamicSort(property: any) {
    return function (a: string, b: string) {
      let aValue, bValue;

      aValue = a[property];
      bValue = b[property];
      if (typeof aValue === "string" && !isValidDate(aValue) && !isValidDate(bValue)) {
        aValue = aValue?.toUpperCase();
        bValue = bValue?.toUpperCase();
      }
      
      if (isValidDate(aValue) && isValidDate(bValue)) {
        const [aDay, aMonth, aYear] = parseDate(aValue);
        const [bDay, bMonth, bYear] = parseDate(bValue);

        aValue = new Date(`${aYear}-${aMonth}-${aDay}`);
        bValue = new Date(`${bYear}-${bMonth}-${bDay}`);
      }
      if (typeof aValue === "string" && !isNaN(Number(aValue))) {
        aValue = Number(aValue);
      }

      if (typeof bValue === "string" && !isNaN(Number(bValue))) {
        bValue = Number(bValue);
      }
      let comparison = 0;

      if (aValue > bValue) {
        comparison = 1;
      } else if (aValue < bValue) {
        comparison = -1;
      }

      return !ascendingToggle ? comparison * -1 : comparison;
    };
  }
  function toggle(index: number) {
    if (accordions && accordions?.includes(index)) {
      setAccordionshow(accordions.filter((num) => num !== index));
    } else {
      setAccordionshow([...(accordions ?? []), index]);
    }
  }

  function isValidDate(dateString: string) {
    const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    return regex.test(dateString);
  }
  function parseDate(dateString: string) {
    const [day, month, year] = dateString.split("/").map(Number);
    return [day, month, year];
  }
  const [sortOn, setsortOn] = useState<string>();
  const sorting = (key: string) => {
    setDataSource(data.sort(dynamicSort(key)));
    if (props.dataToShow) {
      setAccordionshow([]);
      props.dataToShow(data.sort(dynamicSort(key)));
    }
    setsortOn(key);
    setCurrentPage(1);
    setAscendingToggle((prev) => !prev);
  };

  useEffect(() => {
    if (props.expandAll) {
      const expandedIndices: number[] = [];
      dataSource.forEach((_item, index) => {
        if (
          dataSource.filter(
            (item) =>
              props.onAccordionRender &&
              props.onAccordionRender(item).props.children
          ).length > 0
        ) {
          expandedIndices.push(index);
        }
      });
      setAccordionshow(expandedIndices);
    } else {
      setAccordionshow([]);
    }
  }, [props.expandAll]);
    
  return (
    <>
      <div
        ref={table}
        className={`flex flex-col justify-between bg-white px-6 rounded-md text-sm h-full w-full ${
          className ?? ""
        }`}
      >
        <div className="overflow-auto">
          <table className={`w-full ${props.tableClassName}`}>
            <thead
              className={`${props.hederClassName}  ${
                props.hideHeaders ? "collapse" : ""
              }`}
            >
              <tr
                className={`text-left border-b border-[#D1D1D1aa] sticky top-0  bg-primary-500 text-white !tracking-wide !font-normal p-2 ${
                  props.hederClassName
                }  ${
                  props.hideHeaders
                    ? "!bg-transparent !p-0 !h-0 border-none !text-transparent"
                    : ""
                }  ${
                  props.onAccordionRender &&
                  dataSource.filter(
                    (item) =>
                      props.onAccordionRender &&
                      props.onAccordionRender(item).props.children
                  ).length > 0 &&
                  "z-10"
                }`}
              >
                {<th></th>}
                {<th></th>}
                {columnDef &&
                  columnDef.map((item, index) => {
                    return (
                      <th
                        className={`py-2.5 px-3 sticky overflow-hidden top-0 font-normal text-center select-none ${
                          props.hideHeaders
                            ? "!bg-transparent !py-0 !h-0 absolute "
                            : ""
                        }`}
                        key={index}
                      >
                        {!props.hideHeaders && item.sorting ? (
                          <>
                            <div
                              className="flex gap-1 items-center justify-center cursor-pointer group"
                              onClick={() => sorting(item.key)}
                            >
                              {item.onHeaderRender && item.header
                                ? item.onHeaderRender(item["header"])
                                : item["header"]}
                              <img
                                src={SortingICon}
                                className={`${
                                  sortOn == item.key && ascendingToggle
                                    ? "rotate-180"
                                    : ""
                                } ${sortOn == item.key ? " invert" : ""}  ${
                                  props.hideHeaders ? "opacity-0" : ""
                                }`}
                              />
                            </div>
                          </>
                        ) : item.onHeaderRender && item.header ? (
                          item.onHeaderRender(item["header"])
                        ) : (
                          item["header"]
                        )}
                      </th>
                    );
                  })}
              </tr>
            </thead>
            {showBody && (
              <tbody>
                {dataSource.map((item, index) => {
                  return (
                    <>
                      <tr
                        ref={tableRow}
                        className={`border-b border-gray-450/30 ${
                          props.onRowClassName?.(item) || ""
                        }`}
                        key={index}
                      >
                        <td className={`!w-fit ${props.showDropDown?.(
                          item
                        )}`}>
                          {
                            <div className="img w-3.5 h-3.5">
                              <img src={downArrow} className={`hidden`} />
                            </div>
                          }
                        </td>
                        {props.onAccordionRender &&
                        dataSource.filter(
                          (item) =>
                            props.onAccordionRender &&
                            props.onAccordionRender(item).props.children
                        ).length > 0 ? (
                            <td
                              onClick={() => toggle(index)}
                              className={`cursor-pointer w-fit max-w-10  ${props.showDropDownArrow?.(
                                item
                              )}`}
                            >
                              <div className="flex items-center">
                                {props.onAccordionRender(item).props.children && <img
                                  src={downArrow}
                                  className={` w-3.5 min-w-3.5 ${
                                    accordions.includes(index) && "rotate-180 "
                                    
                                  }`}
                                />   }                          
                                {<img
                                  src={SubtaskArrow}
                                  className={"h-5 w-5 min-w-5 hidden " + props.showDropDownImg?.(
                                    item
                                  )}
                                />}
                              </div>
                            </td>
                          ) : (
                            <td className={`!w-fit `}>
                              {
                                <div className="img w-3.5 h-3.5">
                                  <img src={downArrow} className={`hidden`} />
                                </div>
                              }
                              {<img
                                src={SubtaskArrow}
                                className={"h-5 w-5 min-w-5 hidden " + props.showDropDownImg?.(
                                  item
                                )}
                              />}
                            </td>
                          )}
                        {columnDef &&
                          columnDef.map((key: ColumeDef, index: number) => {
                            return (
                              <td
                                className={` py-2 max-md:truncate lg:break-words ${
                                  key.className ? key.className(item) : ""
                                }`}
                                key={key.key}
                                colSpan={ key.colspan ? key.colspan({
                                  index: index,
                                  ...item,
                                }):1}
                              >
                                <div
                                  className={`flex justify-center items-center  ${
                                    dataSource.filter(
                                      (item) =>
                                        props.onAccordionRender &&
                                        props.onAccordionRender(item).props
                                          .children
                                    ).length > 0
                                      ? ""
                                      : ""
                                  }`}
                                >
                                  {key.onCellRender && key.key
                                    ? key.onCellRender({
                                      index: index,
                                      ...item,
                                    })
                                    : item[key.key]}
                                </div>
                              </td>
                            );
                          })}
                      </tr>
                      {accordions?.includes(index) && (
                        <tr
                          className={`transition-all duration-300 ease-in-out transform  ${
                            accordions?.includes(index)
                              ? "opacity-100 scale-100"
                              : "opacity-0 scale-95"
                          }`}
                        >
                          <td
                            colSpan={
                              columnDef?.length
                                ? columnDef?.length + 2
                                : columnDef?.length
                            }
                          >
                            {/* <div
                            className={`rounded-xl flex justify-center ${
                              props.onAccordionRender &&
                              props.onAccordionRender(item).props.children
                                ? " !rounded-none shadow-lg mt-2"
                                : ""
                            } `}
                          > */}
                            {props.onAccordionRender &&
                              props.onAccordionRender(item)}
                            {/* </div> */}
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        {!props.notShowNoDataFound &&dataSource.length == 0 && (
          <div
            className={`w-full ${
              typeof props.noDataFound === "function"
                ? ""
                : "text-center text-xl text-gray-400 font-semibold my-10"
            }`}
          >
            {typeof props.noDataFound === "function"  ? props.noDataFound(data) : t("no_data_found!")}
          </div>
        )}
        <div className="">
          {!props.hidePagination && (
            <div className="justify-between items-center mb-2 flex">
              <div>
                <Select
                  className="w-24"
                  defaultValue={pageOptions.find(
                    (page) => page.value == dataPerPage
                  )}
                  options={pageOptions}
                  menuPlacement="top"
                  styles={reactSelectStyle}
                  onChange={(e) => setDataPerPage(Number(e?.value))}
                />
              </div>
              <div className="flex gap-2 items-center">
                <div className="text-gray-400/80 mr-5">
                  {calculateIndices(currentPage)[0] + 1} -{" "}
                  {calculateIndices(currentPage)[1] > data.length
                    ? data.length
                    : calculateIndices(currentPage)[1]}{" "}
                  of {data.length}
                </div>
                <button
                  className="disabled:opacity-50 "
                  onClick={previousPage}
                  disabled={currentPage <= 1}
                >
                  <img src={arrowImg} className="w-3" />
                </button>
                {currentPage}/{pages}
                <button
                  className="disabled:opacity-50 "
                  onClick={nextPage}
                  disabled={currentPage === pages}
                >
                  <img
                    src={arrowImg}
                    className="rotate-180 w-3"
                    width={18}
                    height={10}
                  />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Table;
